import { useMemo } from 'react'
import type { Abi, Address } from 'viem'

import { WETH } from '../sdk'
import { abi as IUniswapV2PairABI } from '../constants/abis/IUniswapV2Pair.json'
import { ERC20_BYTES32_ABI } from '../constants/abis/erc20'
import ERC20_ABI_JSON from '../constants/abis/erc20.json'
import UNISOCKS_ABI from '../constants/abis/unisocks.json'
import WETH_ABI from '../constants/abis/weth.json'
import { MULTICALL_ABI, MULTICALL_NETWORKS } from '../constants/multicall'

import { useAppChainId } from '../domain/wallet'
import type { ReadContractBinding } from '../state/multicall/contractBinding'

const ERC20_ABI = ERC20_ABI_JSON as Abi

function useReadContractBinding(address: string | undefined, abi: Abi): ReadContractBinding | null {
  return useMemo(() => {
    if (!address || !abi) return null
    return { address: address as Address, abi }
  }, [address, abi])
}

export function useMulticallContract(): ReadContractBinding | null {
  const chainId = useAppChainId()
  const abi = MULTICALL_ABI as Abi

  return useMemo(() => {
    const address = MULTICALL_NETWORKS[chainId]
    if (!address) {
      return null
    }
    return { address: address as Address, abi }
  }, [chainId, abi])
}

/** El tercer argumento histórico (`withSigner`) ya no aplica: todas las lecturas son vía RPC. */
export function useTokenContract(tokenAddress?: string, _legacy?: boolean): ReadContractBinding | null {
  void _legacy
  return useReadContractBinding(tokenAddress, ERC20_ABI)
}

export function useWETHContract(_legacy?: boolean): ReadContractBinding | null {
  void _legacy
  const chainId = useAppChainId()
  return useReadContractBinding(chainId ? WETH[chainId]?.address : undefined, WETH_ABI as Abi)
}

export function useBytes32TokenContract(tokenAddress?: string, _legacy?: boolean): ReadContractBinding | null {
  void _legacy
  return useReadContractBinding(tokenAddress, ERC20_BYTES32_ABI as Abi)
}

export function usePairContract(pairAddress?: string, _legacy?: boolean): ReadContractBinding | null {
  void _legacy
  return useReadContractBinding(pairAddress, IUniswapV2PairABI as Abi)
}

export function useSocksController(): ReadContractBinding | null {
  return useReadContractBinding(undefined, UNISOCKS_ABI as Abi)
}

export type { ReadContractBinding }
